import { SxProps, Theme } from '@mui/material';
import { IMask } from 'react-imask';

import { z } from 'zod';

import {
  Address,
  Company,
  CompanyZone,
  Customer,
  Location,
  NonEmptyArray,
  Note,
  PartialNullable,
  ServiceTitanAppointment,
  TradeEnum
} from '../api/apiTypes.shim';

import { reportException } from './sentryService';

/** Helper that converts possible 'sx' prop shapes into an array (useful for overriding defaults) */
export const toSxArray = (sx?: SxProps<Theme>) => {
  if (Array.isArray(sx)) return sx;
  if (sx) return [sx];
  return [];
};

export const canOrCant = (someTruth: boolean | null | undefined): string => {
  return someTruth ? 'can' : "can't";
};

/**
 *
 * Conditionally return array of elements or empty array
 *
 * Example usage:
 *   const arr = [
 *       ...insertIf(cond, ['a', 'b']),
 *       'c',
 *   ];
 */
export const insertIf = <T>(condition: boolean, elements: T[]) => (condition ? elements : []);

/**
 * Flatten a multidimensional object
 *
 * For example:
 *   flattenObject{ a: 1, b: { c: 2 } }
 * Returns:
 *   { a: 1, c: 2}
 */
const MAX_RECURSION_DEPTH = 100;

export const flattenObject = (obj: object, _depth = 0): object => {
  if (_depth >= MAX_RECURSION_DEPTH) {
    reportException(new Error('flattenObject exceeded max recursion depth'), {
      extra: { maxDepth: MAX_RECURSION_DEPTH }
    });
    return obj;
  }

  const flattened: typeof obj = {};

  Object.keys(obj).forEach((key) => {
    const objKey = key as keyof typeof obj;
    const value = obj[objKey];

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, _depth + 1));
    } else {
      flattened[objKey] = value;
    }
  });

  return flattened;
};

/**
 * Pick specific properties from an object, supporting deeply nested paths.
 *
 * @example
 * pickDeeplyNested(
 *   { name: 'foo', callCenter: { vendorPhoneNumber: '123', other: 'bar' } },
 *   ['name', 'callCenter.vendorPhoneNumber']
 * )
 * // Returns: { name: 'foo', callCenter: { vendorPhoneNumber: '123' } }
 *
 * @param item - The source object to pick from
 * @param keys - Array of property paths (can be nested like 'a.b.c')
 * @returns A new object containing only the specified paths
 */
export function pickDeeplyNested<T extends object, K extends string>(item: T, keys: readonly K[]): Partial<T> {
  const result: Record<string, unknown> = {};

  for (const key of keys) {
    const pathParts = key.split('.');

    // Get the value from the source object
    let value: unknown = item;
    let exists = true;

    for (const part of pathParts) {
      if (value && typeof value === 'object' && part in value) {
        value = (value as Record<string, unknown>)[part];
      } else {
        exists = false;
        break;
      }
    }

    // Set the value in the result object if it exists
    if (exists) {
      let current: Record<string, unknown> = result;

      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i]!;
        if (!(part in current)) {
          current[part] = {};
        }
        current = current[part] as Record<string, unknown>;
      }

      const lastPart = pathParts[pathParts.length - 1]!;
      current[lastPart] = value;
    }
  }

  return result as Partial<T>;
}

/**
 * Recursively maps a type so that every `string` becomes `string | null`.
 * Arrays, Dates, RegExps, Maps, and Sets are preserved; plain objects are mapped recursively.
 */
type WithEmptyStringsAsNull<T> = T extends string
  ? string | null
  : T extends (infer U)[]
    ? WithEmptyStringsAsNull<U>[]
    : T extends Date | RegExp | Map<unknown, unknown> | Set<unknown>
      ? T
      : T extends object
        ? { [K in keyof T]: WithEmptyStringsAsNull<T[K]> }
        : T;

/**
 * Recursively replace all empty string values with null in an object or array.
 *
 * @example
 * replaceEmptyStringsWithNull({ name: '', age: 25, address: { street: '', city: 'NYC' } })
 * // Returns: { name: null, age: 25, address: { street: null, city: 'NYC' } }
 *
 * @param obj - The object or array to process
 * @returns A new object/array with empty strings replaced by null
 */
export function replaceEmptyStringsWithNull<T>(obj: T, _depth = 0): WithEmptyStringsAsNull<T> {
  if (_depth >= MAX_RECURSION_DEPTH) {
    reportException(new Error('replaceEmptyStringsWithNull exceeded max recursion depth'), {
      extra: { maxDepth: MAX_RECURSION_DEPTH, obj }
    });
    return obj as WithEmptyStringsAsNull<T>;
  }

  if (
    obj === null ||
    obj === undefined ||
    obj instanceof Date ||
    obj instanceof RegExp ||
    obj instanceof Map ||
    obj instanceof Set
  ) {
    return obj as WithEmptyStringsAsNull<T>;
  }

  if (obj === '') {
    return null as WithEmptyStringsAsNull<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceEmptyStringsWithNull(item, _depth + 1)) as WithEmptyStringsAsNull<T>;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = replaceEmptyStringsWithNull((obj as Record<string, unknown>)[key], _depth + 1);
      }
    }
    return result as WithEmptyStringsAsNull<T>;
  }

  return obj as WithEmptyStringsAsNull<T>;
}

/** Ensures every path in `paths` exists as a key in `obj`, defaulting to undefined */
export function ensureKeys<T extends object>(obj: T, paths: readonly string[]): T {
  const result = structuredClone(obj);
  for (const path of paths) {
    const parts = path.split('.');
    let current: Record<string, unknown> = result as Record<string, unknown>;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]!;
      if (!(part in current)) current[part] = {};
      current = current[part] as Record<string, unknown>;
    }
    const last = parts[parts.length - 1]!;
    if (!(last in current)) current[last] = undefined;
  }
  return result;
}

export function isStringArray(obj: unknown): obj is string[] {
  if (!Array.isArray(obj)) {
    return false;
  }
  return obj.every((item) => typeof item === 'string');
}

interface FormatAddressOptions {
  includeCountry?: boolean;
  /**
   * Value to display if sanitizing address properties results in an empty string
   *  @default 'Unknown Address'
   */
  emptyDisplayValue?: string;
}

const trimmedString = (val?: string | null) => (val ? val.trim() : '');

const ADDRESS_EMPTY_DISPLAY_VALUE = 'Unknown Address';

type AddressWithoutEnums = PartialNullable<
  Omit<Address, 'stateOrProvince' | 'country'> & { stateOrProvince: string; country: string }
>;

export const formatAddressOneLine = <T extends AddressWithoutEnums>(obj: T, options?: FormatAddressOptions): string => {
  const country = options?.includeCountry ? trimmedString(obj.country) : '';
  return (
    [
      trimmedString(obj.addressLine1),
      trimmedString(obj.addressLine2),
      trimmedString(obj.city),
      `${trimmedString(obj.stateOrProvince)} ${trimmedString(obj.zipOrPostal)} ${country}`.trim()
    ]
      .filter((v) => !!v)
      .join(', ') ||
    options?.emptyDisplayValue ||
    ADDRESS_EMPTY_DISPLAY_VALUE
  );
};

export const formatAddressTwoLine = <T extends AddressWithoutEnums>(obj: T, options?: FormatAddressOptions): string => {
  const firstLine = [trimmedString(obj.addressLine1), trimmedString(obj.addressLine2)].filter((v) => !!v).join(', ');
  const country = options?.includeCountry ? trimmedString(obj.country) : '';
  const secondLine = [
    trimmedString(obj.city),
    `${trimmedString(obj.stateOrProvince)} ${trimmedString(obj.zipOrPostal)} ${country}`.trim()
  ]
    .filter((v) => !!v)
    .join(', ');

  if (firstLine && secondLine) return `${firstLine}\n${secondLine}`;
  else if (firstLine) return `${firstLine}\nUNKNOWN City/State/Zip`;
  else if (secondLine) return `UNKNOWN Street Address\n${secondLine}`;
  else return options?.emptyDisplayValue || ADDRESS_EMPTY_DISPLAY_VALUE;
};

export const phoneMaskSpec = {
  mask: '#00-000-0000',
  definitions: {
    '#': /[1-9]/
  }
};

export const formatPhone = (phone: string | null): string => {
  const phoneMask = IMask.createMask([
    {
      mask: '#00-0000',
      definitions: {
        '#': /[1-9]/
      }
    },
    phoneMaskSpec
  ]);

  if (phone) {
    phoneMask.resolve(phone);
    return phoneMask.value;
  }
  return '';
};

type NameItem = { firstName: string | null; lastName: string | null };

export const getFullName = ({ firstName, lastName }: NameItem) =>
  `${firstName?.trim() || ''} ${lastName?.trim() || ''}`.trim();

export const customerDisplayName = (
  customer: Pick<Customer, 'isCommercial' | 'companyName' | 'firstName' | 'lastName'>
): string => {
  if (customer.isCommercial && customer.companyName) return customer.companyName.trim();
  const fullName = getFullName(customer);
  return fullName.length > 0 ? fullName : 'Unknown';
};

export const isValidPhone10 = (value: string | null | undefined): value is string =>
  !!value && value.length === 10 && /^\d+$/.test(value);

export const hasTenDigits = (val: string | null | undefined) => !!val && val.replace(/[^0-9]+/g, '').length > 9;

/**
 * @returns 10-digit phone number IF valid. Otherwise, returns null
 */
export const parsePhone10 = (value: string | null | undefined): string | null => {
  let digitString = (value || '').replace(/[^0-9]+/g, '');
  if (digitString.length === 11 && digitString[0] === '1') digitString = digitString.slice(1);
  return digitString.length === 10 ? digitString : null;
};

export const parseIsoString = (value: string): Date | null => {
  const schema = z.coerce.date();
  const result = schema.safeParse(value);
  if (result.success) {
    return result.data;
  } else {
    return null;
  }
};

export const sortedNotes = (notes: Note[]) => [...notes].sort((a, b) => (a.modifiedAt < b.modifiedAt ? 1 : -1));

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** @returns the appointment number OR 'jobNumber-X' */
const getNormalizedAppointmentNumber = (appointmentNumber: string | null, jobNumber: string) =>
  appointmentNumber ? appointmentNumber : `${jobNumber || 'XXXX'}-X`;

/** @returns '#appointmentNumber - appointmentStatus' - NOTE the appointmentNumber is normalized to 'jobNumber-x' if it is null */
export const getAppointmentNumberAndStatus = (
  appointment: Pick<ServiceTitanAppointment, 'appointmentNumber' | 'status'>,
  jobNumber: string
) => `#${getNormalizedAppointmentNumber(appointment.appointmentNumber, jobNumber)} - ${appointment.status}`;

export const getDefaultTrade = (company: Company) =>
  company.callCenter.trades.length === 1 ? company.callCenter.trades[0] : null;

export const getTradeForNewCompany = (trade: TradeEnum | null | undefined, newCompany: Company) =>
  trade && newCompany.callCenter.trades.includes(trade) ? trade : getDefaultTrade(newCompany);

export type NonEmptyDnisArray = NonEmptyArray<Company['callCenter']['dnisArray'][0]>;

export const isNonEmptyDnisArray = (dnisArray: Company['callCenter']['dnisArray']): dnisArray is NonEmptyDnisArray => {
  return dnisArray.length > 0;
};

export function isNonEmptyArray<T>(arr: T[]): arr is NonEmptyArray<T> {
  return arr.length > 0;
}

/**
 * @returns whether or not each location is deeply equal
 */
export const isLocationEqual = (l1: Location, l2: Location): boolean => {
  for (const locationKey of Object.keys(l1)) {
    if (l1[locationKey as keyof Location] !== l2[locationKey as keyof Location]) return false;
  }
  return true;
};

export const getOnCallNotes = (company: Company, selectedZone?: CompanyZone | null) => {
  if (!company.isZoned) return company.onCallNotes || 'Company does not have any on call instructions.';
  if (!selectedZone) return 'Company Zone must be selected to display proper on call instructions.';
  return selectedZone.onCallNotes || 'Company Zone for call does not have any on call instructions.';
};

/**
 * Recursively extracts all error messages from a nested error object,
 * filtering out errors whose paths match the provided formValuePaths.
 *
 * @example
 * extractUnmappableErrors(
 *   { name: { message: 'Required' }, callCenter: { phone: { message: 'Invalid' } }, nonFieldErrors: { message: 'General error' } },
 *   ['name']
 * )
 * // Returns: ['callCenter.phone: Invalid', 'General error']
 *
 * @param errors - The error object from react-hook-form onInvalid handler
 * @param formValuePaths - Array of field paths that are controlled by the current form
 * @returns Array of error messages for fields not in formValuePaths, plus nonFieldErrors
 */
export function extractUnmappableErrors(errors: Record<string, unknown>, formValuePaths: readonly string[]): string[] {
  const errorMessages: string[] = [];

  /**
   * Recursively traverse the error object and collect error messages
   */
  function traverse(obj: unknown, currentPath: string[] = [], _depth = 0): void {
    if (_depth >= MAX_RECURSION_DEPTH) {
      reportException(new Error('extractUnmappableErrors exceeded max recursion depth'), {
        extra: { maxDepth: MAX_RECURSION_DEPTH, obj, currentPath: currentPath.join('.') }
      });
      return;
    }

    if (!obj || typeof obj !== 'object') return;

    const errorObj = obj as Record<string, unknown>;

    // Handle special case: nonFieldErrors
    if (currentPath.length === 0 && 'nonFieldErrors' in errorObj) {
      const nonFieldErrors = errorObj.nonFieldErrors;
      if (nonFieldErrors && typeof nonFieldErrors === 'object' && 'message' in nonFieldErrors) {
        const message = (nonFieldErrors as { message: unknown }).message;
        if (typeof message === 'string') {
          errorMessages.push(message);
        }
      }
    }

    // Process all keys in the error object
    for (const key of Object.keys(errorObj)) {
      if (key === 'nonFieldErrors') continue; // Already handled above

      const value = errorObj[key];
      const newPath = [...currentPath, key];
      const pathString = newPath.join('.');

      // Check if this path matches any formValuePath
      const isInFormValuePaths = formValuePaths.some((formPath) => {
        // Check exact match or if it's a parent path
        return pathString === formPath || pathString.startsWith(`${formPath}.`);
      });

      // If this is an error message object
      if (value && typeof value === 'object') {
        const valueObj = value as Record<string, unknown>;
        const hasMessage = 'message' in valueObj;
        const keys = Object.keys(valueObj);
        const hasOtherKeys = keys.some((k) => k !== 'message');

        // If this is an error message object, collect its message
        if (hasMessage) {
          const message = (valueObj as { message: unknown }).message;
          if (typeof message === 'string' && !isInFormValuePaths) {
            errorMessages.push(`${pathString}: ${message}`);
          }
        }

        // If there are nested properties beyond `message`, traverse deeper
        if (hasOtherKeys) {
          traverse(valueObj, newPath, _depth + 1);
        }
      }
    }
  }

  traverse(errors);
  return errorMessages;
}

/**
 * Recursively extracts all error messages from a nested error object
 *
 * @example
 * extractAllErrors(
 *   { name: { message: 'Required' }, callCenter: { phone: { message: 'Invalid' } }, nonFieldErrors: { message: 'General error' } },
 * )
 * // Returns: ['name: Required', 'callCenter.phone: Invalid', 'General error']
 *
 * @param errors - The error object from react-hook-form onInvalid handler
 * @returns Array of error messages
 */
export function extractAllErrors(errors: Record<string, unknown>): string[] {
  return extractUnmappableErrors(errors, []); // Just call extractUnmappableErrors with an empty formValuePaths to get all errors
}
